import {
  useGetUserByIdQuery,
  useRegisterUserMutation,
} from "@/services/UserApi";
export interface ProfileInfoProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  ariaLabel?: string;
}

export interface UserDetailsProps {
  label: string;
  value: string;
  ariaLabel?: string;
}

export interface ProfileCardProps {
  name: string;
  title: string;
  avatar: string;
  email: string;
  phone: string;
  gender: string;
  country: string;
  onProfileClick?: () => void;
  onSettingsClick?: () => void;
}

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/MobileToolTip";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flag } from "lucide-react";
import { messaging } from "../../../utils/lib/Firebase";
import { Messaging, onMessage } from "firebase/messaging";
import { toast } from "../../ui/UseToast";

export const ProfileInfo: React.FC<ProfileInfoProps> = ({
  icon,
  label,
  value,
  ariaLabel,
}) => {
  return (
    <div
      className="flex gap-5 self-start"
      role="group"
      aria-label={ariaLabel || `${label} information`}
    >
      <div className="flex flex-col my-auto">
        {typeof icon === 'string' ? (
          <img
            loading="lazy"
            src={icon}
            alt=""
            className="object-contain w-5 aspect-square stroke-[2px] stroke-yellow-600"
            aria-hidden="true"
          />
        ) : (
          icon
        )}
      </div>
      <div className="flex flex-col items-start text-sm font-semibold leading-none text-neutral-800">
        <div
          className="tracking-normal capitalize"
          id={`${label.toLowerCase()}-label`}
        >
          {label}
        </div>
        <div
          className="text-xs leading-snug text-yellow-600"
          aria-labelledby={`${label.toLowerCase()}-label`}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

export const UserDetails: React.FC<UserDetailsProps> = ({
  label,
  value,
  ariaLabel,
}) => {
  return (
    <div
      className="flex flex-col my-auto"
      role="group"
      aria-label={ariaLabel || `${label} details`}
    >
      <div
        className="text-sm font-semibold tracking-normal leading-none capitalize text-neutral-800"
        id={`${label.toLowerCase()}-label`}
      >
        {label}
      </div>
      <div
        className="mt-1.5 text-xs leading-snug text-yellow-600"
        aria-labelledby={`${label.toLowerCase()}-label`}
      >
        {value}
      </div>
    </div>
  );
};

export const ProfileCard: React.FC<ProfileCardProps> = ({
  name,
  title,
  avatar,
  email,
  phone,
  gender,
  country,
  onProfileClick,
  onSettingsClick,
}) => {

  const navigate=useNavigate();
  return (
    <div className="flex flex-col rounded-none w-full" role="article">
      <div className="flex flex-col justify-start items-center gap-5 self-center w-full text-base font-semibold tracking-normal leading-none capitalize whitespace-nowrap max-w-[500px] text-neutral-800">
        <div className="flex gap-4">
          <img
            loading="lazy"
            src={avatar}
            alt={`${name}'s profile picture`}
            className="object-contain shrink-0 aspect-square rounded-[100px] w-[63px]"
          />
          <div className="flex flex-col my-auto">
            <div
              className="self-start text-base font-semibold tracking-wide leading-7 text-neutral-800"
              role="heading"
              aria-level={2}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      {name?.length > 16 ? `${name.slice(0, 15)}...` : name}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* <i className="ml-2 rounded-full p-2 border-2 border-black fa-solid fa-pencil"></i>{" "} */}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 mt-1">
           {/* <ProfileInfo
            icon={<Flag className="text-primary" />}
            label="Country"
            value={country}
            ariaLabel="Phone contact information"
          /> */}
          <ProfileInfo
            icon="https://cdn.builder.io/api/v1/image/assets/7b641f74ca3a451fab2d8c8d5a563bae/198fa2c6fb2b13e15a1d8b87a87208d6059507a28f203bce0ed45db68d4d2a47?apiKey=7b641f74ca3a451fab2d8c8d5a563bae&"
            label="Email"
            value={email}
            ariaLabel="Email contact information"
          />
          <ProfileInfo
            icon="https://cdn.builder.io/api/v1/image/assets/7b641f74ca3a451fab2d8c8d5a563bae/06a3a8d91d7f65af30b56fc9cf8f929ec6395c5bf3ac4094f694d5b0d39b1e3d?apiKey=7b641f74ca3a451fab2d8c8d5a563bae&"
            label="Phone"
            value={phone}
            ariaLabel="Phone contact information"
          />
          <ProfileInfo
            icon={<Flag className="text-primary" />}
            label="Country"
            value={country}
            ariaLabel="Phone contact information"
          />
        </div>
      </div>
      <div
        className="flex mt-6 w-full bg-gray-200 border border-gray-200 border-solid min-h-[1px]"
        role="separator"
      />
    </div>
  );
};



const ProfileInfoSkeleton = () => (
  <div className="flex gap-5 self-start animate-pulse">
    <div className="flex flex-col my-auto w-5 h-5 bg-gray-300 rounded-full"></div>
    <div className="flex flex-col items-start text-sm font-semibold leading-none text-neutral-800">
      <div className="w-20 h-4 bg-gray-300 rounded mb-2"></div>
      <div className="w-24 h-3 bg-gray-300 rounded"></div>
    </div>
  </div>
);

// Skeleton for Profile Card
const ProfileCardSkeleton = () => (
  <div className="flex flex-col rounded-none w-full animate-pulse">
    <div className="flex gap-5 justify-between self-center w-full text-base font-semibold tracking-normal leading-none capitalize whitespace-nowrap max-w-[500px] text-neutral-800">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
        <div className="w-24 h-4 bg-gray-300 rounded"></div>
      </div>
      <div className="w-9 h-9 bg-gray-300 rounded-full"></div>
    </div>

    <div
      className="flex mt-6 mb-2 w-full bg-gray-200 border border-gray-200 border-solid min-h-[1px]"
      role="separator"
    />

    <div className="flex flex-col justify-start items-center gap-5 self-center w-full text-base font-semibold tracking-normal leading-none capitalize whitespace-nowrap max-w-[500px] text-neutral-800">
      <div className="flex gap-4">
        <div className="w-16 h-16 bg-gray-300 rounded-full"></div>
        <div className="flex flex-col my-auto">
          <div className="w-32 h-5 bg-gray-300 rounded"></div>
        </div>
      </div>
      <div className="flex flex-col gap-4 mt-1">
        <ProfileInfoSkeleton />
        <ProfileInfoSkeleton />
        <ProfileInfoSkeleton />
      </div>
    </div>
    <div
      className="flex mt-6 w-full bg-gray-200 border border-gray-200 border-solid min-h-[1px]"
      role="separator"
    />
  </div>
);

export const EditProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();

  // Fetch user data
  const { data: user, isLoading } = useGetUserByIdQuery(Number(userId));
   React.useEffect(() => {
     let unsubscribe: (() => void) | null = null;

     messaging().then((msgInstance: Messaging | null) => {
       if (!msgInstance) {
         console.error("Firebase Messaging is not initialized.");
         return;
       }

       unsubscribe = onMessage(msgInstance, (payload) => {
          if (payload.notification) {
                    toast({
                      variant: "default",
                      description: payload.notification.body,
                      title: payload.notification.title,
                    });
                    
                  }
         const { data } = payload;
         if (data?.type && data?.id) {
          
         }
       });
     });

     return () => {
       if (unsubscribe) unsubscribe();
     };
   }, []);
  // Handlers for profile and settings click
  const handleProfileClick = React.useCallback(() => {
  }, []);

  const handleSettingsClick = React.useCallback(() => {
  }, []);

  // If loading, show the skeleton loader
  if (isLoading) {
    return <ProfileCardSkeleton />;
  }

  return (
    <ProfileCard
      name={`${user?.first_name ?? ""} ${user?.last_name ?? ""}`}
      title="Marketing Administrator"
      avatar="https://cdn.builder.io/api/v1/image/assets/7b641f74ca3a451fab2d8c8d5a563bae/20dfef61fd08b535cc408b9042ed7fe9a17d37444c695095e43bd7bcca31c546?apiKey=7b641f74ca3a451fab2d8c8d5a563bae&"
      email={user?.email ?? ""}
      phone={user?.phone_num ?? " "}
      gender={user?.gender ?? " "}
      country="Pakistan"
      onProfileClick={handleProfileClick}
      onSettingsClick={handleSettingsClick}
    />
  );
};
